<template>
  <div>
    <div class="d-flex justify-content-between">
      <div class="d-flex justify-content-left button-group">
        <b-button v-if="!isNewOrder" @click="openHistoryModal" class="ml-0">{{$t('buttons.common.history')}}</b-button>
        <order-item-create-modal :supplier="purchaseOrderData.shipper" :purchaseOrderID="this.param"></order-item-create-modal>
        <history-modal-content></history-modal-content>
        <b-button v-if="!isNewOrder" @click="openPrintModal" class="margin">{{$t('buttons.common.print')}}</b-button>
        <print-document-modal></print-document-modal>
      </div>
      <div v-if="currentRoutePermissions.change_purchaseorder">
        <b-button class="btn" variant="secondary" v-if="!isNewOrder" @click="saveOrderAsNew" size="sm">
          {{$t('buttons.common.save_as_new')}}
        </b-button>
        <b-button class="btn" variant="primary" v-if="!isNewOrder && !priceUsageIsPriceList" @click="createRequest" size="sm">
          {{$t('buttons.requests.create_request')}}
        </b-button>
        <b-button class="btn" variant="success" v-if="!isNewOrder && isActiveOrder" @click="completeOrder" size="sm">
          {{$t('buttons.purchase_orders.complete_order')}}
        </b-button>
        <b-button class="btn" variant="success" v-if="!isNewOrder && !isActiveOrder" @click="recoverOrder" size="sm">
          {{$t('buttons.purchase_orders.recover_order')}}
        </b-button>
        <b-button class="btn" variant="secondary" v-if="isActiveOrder" @click="save" size="sm">
          {{$t('buttons.common.save')}}
        </b-button>
      </div>
    </div>
    <div class="d-flex justify-content-between toggle-container" @click="showCollapse = !showCollapse"
         :aria-expanded="showCollapse ? 'true' : 'false'"
         aria-controls="collapse">
      <h3>
        {{purchaseOrderLabel}}
        <span v-if="!isNewOrder"
              :style="{color: isActiveOrder ? 'green' : 'red'}"> - {{purchaseOrderData.status}}</span>
      </h3>
      <div class="icons-wrapper">
        {{purchaseOrderInfo}}
        <icon name="angle-down" class="angle" v-if="showCollapse"></icon>
        <icon name="angle-right" class="angle" v-else></icon>
      </div>
    </div>
    <b-collapse id="collapse" v-model="showCollapse">
      <div class="search-form">
        <div class="search-form__filters">
          <b-row>
            <b-col>
              <label>{{$t('labels.common.supplier')}}</label>
              <multiselect v-model="purchaseOrderData.shipper" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :class="{ required: (!purchaseOrderData.shipper || purchaseOrderData.shipper.length === 0) }"
                           :options="suppliers" :disabled="!isActiveOrder"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.contracts.contract_number')}}</label>
              <multiselect v-model="purchaseOrderData.contracts" :max-height="200" label="contract_nr"
                           :options="[]" :multiple="true" :disabled="true" placeholder=""
                           :taggable="true" :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.measure_type')}}</label>
              <multiselect v-model="purchaseOrderData.measuries" :max-height="200"
                           :options="measure_types" :disabled="true" :multiple="true" placeholder=""
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="purchaseOrderData.buyer" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :class="{ required: (!purchaseOrderData.buyer || purchaseOrderData.buyer.length === 0) }"
                           :options="customers" :disabled="!isActiveOrder"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.delivery_term')}}</label>
              <multiselect v-model="purchaseOrderData.incoterms" :max-height="200"
                           placeholder="" label="code" track-by="id"
                           :options="incoterms" :disabled="!isActiveOrder"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.currency')}}</label>
              <multiselect v-model="purchaseOrderData.currencies" :max-height="200"
                           :options="[]" :disabled="true" :multiple="true" placeholder=""
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.common.agent')}}</label>
              <multiselect v-model="purchaseOrderData.agent" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="agents" :disabled="!isActiveOrder"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.transport')}}</label>
              <multiselect v-model="purchaseOrderData.delivery_method" :max-height="200"
                           placeholder="" label="name" track-by="id"
                           :options="delivery_methods" :disabled="!isActiveOrder"
                           :taggable="true" :close-on-select="true"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.notes')}}</label>
              <b-form-textarea v-model="purchaseOrderData.notes"
                               :disabled="!isActiveOrder"
                               placeholder="">
              </b-form-textarea>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="4">
              <label>{{$t('labels.common.delivery_date')}}</label>
              <datepicker v-model="purchaseOrderData.delivery_term" lang="en"
                          :class="{ required: (!purchaseOrderData.delivery_term)}"
                          :disabled="!isActiveOrder"
                          placeholder=""
                          :first-day-of-week="1"
              ></datepicker>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-collapse>
    <b-card no-body v-if="!isNewOrder">
      <b-tabs card>
        <b-tab active>
          <template slot="title">
            <portal-target name="order_items"></portal-target>
          </template>
          <portal to="order_items">
            {{$t('tabs.purchase_orders.order_items')}}
          </portal>
          <div class="d-flex justify-content-between button-group">
            <div>
              {{$t('labels.common.selected')}}: {{selectedRowsCount}}
              <b-button v-if="isActiveOrder && currentRoutePermissions.change_purchaseorder" class="btn"
                        @click="deleteOrderItem" variant="danger" size="sm">
                {{$t('buttons.purchase_orders.delete_item')}}
              </b-button>
            </div>
            <b-button v-if="isActiveOrder && currentRoutePermissions.change_purchaseorder" class="btn" variant="success"
                      @click="openOrderItemModal" size="sm">
              {{$t('buttons.purchase_orders.create_order_item')}}
            </b-button>
          </div>
          <ag-grid-vue class="ag-theme-balham"
                       :rowData="orderItems"
                       :style="gridHeight"
                       :columnDefs="orderItemsColumns"
                       :gridOptions="orderItemsGridOptions" :pinnedBottomRowData="pinnedRowData"
                       @rowSelected="countSelectedRows" @rowDataChanged="countSelectedRows"
                       @rowDoubleClicked="openOrderItemModal" :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-tab>
        <b-tab @click="loadContractAndAcceptancesTab">
          <template slot="title">
            <portal-target name="contracts_and_acceptances"></portal-target>
          </template>
          <portal to="contracts_and_acceptances">
            {{$t('tabs.purchase_orders.contracts_and_acceptances')}}
          </portal>
          <ag-grid-vue class="ag-theme-balham grid"
                       :rowData="progressPerMovement"
                       :style="gridHeight"
                       :columnDefs="progressPerMovementColumns"
                       :gridOptions="gridOptions"
                       :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="docs"></portal-target>
          </template>
          <portal to="docs">
            {{$t('tabs.common.documents')}}
          </portal>
          <app-documents :documents="documents" :isCollapse="showCollapse" :url="commonUrl + 'attachments/'"
                         @loadDocuments="loadDocuments"></app-documents>
        </b-tab>
        <b-tab>
          <template slot="title">
            <portal-target name="comment"></portal-target>
          </template>
          <portal to="comment">
            {{$t('tabs.common.comments')}}
          </portal>
          <app-comments :comments="comments" :url="commonUrl + 'comments/'"
                        @loadComments="loadComments"></app-comments>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { mapGetters, mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import Datepicker from 'vue2-datepicker'
import RangeDatePicker from 'vue2-datepicker'
import moment from 'moment'
import OrderItemCreateModal from '@/components/modals/PurchaseOrderItemModal'
import HistoryModalContent from '@/components/modals/HistoryModal'
import PrintDocumentModal from '@/components/modals/PrintDocumentModal.vue'
import Documents from '@/components/common/Documents'
import Comments from '@/components/common/Comments'
import { getArrayByProperty, handlerResponse, setNotifyData } from '@/helpers/commonHelpers'
import { notificationsRenderer } from '@/helpers/renderHelpers'
import { gridMixins } from '@/mixins'
import { DATE_FORMAT } from '@/constants'

export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      RangeDatePicker,
      'ag-grid-vue':
      AgGridVue,
      Datepicker,
      OrderItemCreateModal,
      PrintDocumentModal,
      HistoryModalContent,
      'app-documents': Documents,
      'app-comments': Comments
    },
    data() {
      return {
        showCollapse: true,
        selectedRowsCount: 0,
        searchObject: {
          limit: 15,
          offset: 0
        },
        purchaseOrderData: {
          created: '',
          created_by: '',
          order_nr: '',
          measuries: '',
          delivery_term: null,
          currencies: '',
          shipper: '',
          contracts: [],
          buyer: '',
          incoterms: '',
          notes: '',
          delivery_method: '',
          agent: ''
        }
      }
    },
    methods: {
      save() {
        const saveData = {
          ...this.purchaseOrderData,
          buyer: this.purchaseOrderData.buyer ? this.purchaseOrderData.buyer.id : '',
          incoterms: this.purchaseOrderData.incoterms ? this.purchaseOrderData.incoterms.id : '',
          shipper: this.purchaseOrderData.shipper ? this.purchaseOrderData.shipper.id : '',
          agent: this.purchaseOrderData.agent ? this.purchaseOrderData.agent.id : '',
          delivery_method: this.purchaseOrderData.delivery_method ? this.purchaseOrderData.delivery_method.id : '',
          delivery_term: this.purchaseOrderData.delivery_term ? moment(this.purchaseOrderData.delivery_term).format(DATE_FORMAT) : ''
        }
        if (this.isNewOrder) {
          this.$http.post(`purchase/orders/`, saveData)
            .then(handlerResponse).then((response) => {
              this.purchaseOrderData = {...response};
              this.getOrderInfo(response.id);
              this.$notify(setNotifyData('Success', 'Order has been created!', 'success'));
              this.$router.push({path: `/purchaseOrders/${response.id}`});
            }).catch((error) => {
              console.log(error);
              notificationsRenderer(error.body, 'error');
            })
        } else {
          this.$http.put(`purchase/orders/${this.param}/`, saveData).then(handlerResponse).then(() => {
            this.getOrderInfo(this.param);
            this.$notify(setNotifyData('Success', 'Purchase order has been saved!', 'success'));
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          });
        }
      },
      completeOrder() {
        this.$http.put(`purchase/orders/${this.param}/`, {status: 'completed'}).then(handlerResponse).then(() => {
          this.$router.push({path: `/purchaseOrders/`});
          this.$notify(setNotifyData('Success', 'Purchase order has been completed!', 'success'));
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.body, 'error');
        });
      },
      recoverOrder() {
        this.$http.put(`purchase/orders/${this.param}/`, {status: 'active'}).then((response) => {
          this.getOrderInfo(response.body.id);
          this.$notify(setNotifyData('Success', 'Purchase order has been recovered!', 'success'));
        }).catch((error) => {
          this.$notify(setNotifyData('', 'Error while recovering the purchase order', 'error'));
        });
      },
      deleteOrderItem() {
        if (this.orderItemsGridOptions.api.getSelectedRows().length) {
          const id = getArrayByProperty(this.orderItemsGridOptions.api.getSelectedRows(), 'id');
          this.$http.delete(`purchase/order_items/`, {body: {id}}).then((response) => {
            this.$store.dispatch('purchaseOrderItems/loadOrderItems', {order_id: this.param});
            this.$notify(setNotifyData('Success', response.body, 'success'));
          }).catch((error) => {
            console.log(error);
            this.$notify(setNotifyData('', error.body, 'error'));
          });
        } else {
          notificationsRenderer(error.body, 'error');
        }
      },
      countSelectedRows() {
        this.selectedRowsCount = getArrayByProperty(this.orderItemsGridOptions.api.getSelectedRows(), 'id') ? getArrayByProperty(this.orderItemsGridOptions.api.getSelectedRows(), 'id').length : 0
      },
      createRequest() {
        this.checkForOrderItems().then(() => {
          this.$http.post(`purchase/orders/${this.param}/add_request_order/`, {id: this.param})
            .then(handlerResponse).then((response) => {
            this.$router.push({path: `/requests/${response}`});
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        })
      },
      saveOrderAsNew() {
        this.checkForOrderItems().then(() => {
          this.$http.post(`purchase/orders/${this.param}/duplicate/`, {id: this.param})
            .then(handlerResponse).then((response) => {
            this.$router.push({path: `/purchaseOrders/${response}`})
            this.getOrderInfo(response);
          }).catch((error) => {
            console.log(error);
            notificationsRenderer(error.body, 'error');
          })
        })
      },
      checkForOrderItems() {
        return new Promise((resolve) => {
          const completedOrderItems = this.orderItems.filter(e => e.lot_status === 'completed');
          if (completedOrderItems.length) {
            this.$swal.fire({
              title: 'Warning!',
              text: 'Some order items\'re created to completed lots. You want to continue only with order items with active lots?',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#55d60a',
              cancelButtonColor: '#d33',
              confirmButtonText: 'Yes',
              cancelButtonText: 'No'
            }).then((result) => {
              if (result.value) {
                resolve();
              }
            })
          } else {
            resolve();
          }
        })
      },
      loadDocuments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('purchaseOrders/loadDocumentsList', {id: this.param});
        } else {
          this.$store.dispatch('purchaseOrders/loadDocumentsList', {id: this.param});
        }
      },
      loadComments(cacheFlag) {
        if (cacheFlag) {
          this.$store.dispatch('purchaseOrders/loadCommentsList', {id: this.param});
        } else {
          this.$store.dispatch('purchaseOrders/loadCommentsList', {id: this.param});
        }
      },
      async loadContractAndAcceptancesTab() {
        if (this.purchaseOrderData.contracts.length) {
          const id = getArrayByProperty(this.purchaseOrderData.contracts, 'id')
          await this.$store.dispatch('purchaseOrders/loadBindedMovementProgress', {id: this.param, contract_ids: id});
        }
      },
      openOrderItemModal(params) {
        if (this.currentRoutePermissions.change_purchaseorder) {
          if (params.data) {
            this.$http.get(`purchase/order_items/${params.data.id}/`).then(handlerResponse).then((response) => {
              let defaultData = {
                ...response,
                isNewItem: false
              };
              if (!this.priceUsageIsPriceList) {
                defaultData = {
                  ...defaultData,
                  qualities: response.qualities[0],
                  humidities: response.humidities[0],
                  profiles: response.profiles[0]
                }
              }
              this.$root.$emit('openCreateOrderItemModal', defaultData);
            });
          } else {
            const defaultData = {
              isNewItem: true,
              delivery_term: this.purchaseOrderData.delivery_term
            };
            this.$root.$emit('openCreateOrderItemModal', defaultData);
          }
        }
      },
      getOrderInfo(id) {
        this.$http.get(`purchase/orders/${id}/`).then(handlerResponse).then((response) => {
          this.$store.dispatch('purchaseOrderItems/loadOrderItems', {order_id: id});
          // we should not put in orderData contracts if price usage is 'price_list'
          this.purchaseOrderData = response.shipper?.price_usage === 'price_list'
              ? { ...response, contracts: [] }
              : response;
          this.$store.dispatch('location/setPathName', response.order_nr);
        }).catch(() => {
          this.$router.push({path: `/purchaseOrders/new`});
          this.$notify(setNotifyData('', 'Purchase order with such ID has been not found. You can create new one', 'error'));
        });
      },
      clearData() {
        this.$store.cache.delete('purchaseOrders/loadDocumentsList', {id: this.param});
        this.$store.cache.delete('purchaseOrders/loadCommentsList', {id: this.param});
        if (this.comments.length) {
          this.$store.commit('purchaseOrders/setCommentsList', []);
        }
        if (this.documents.length) {
          this.$store.commit('purchaseOrders/setDocumentsList', []);
        }
        this.$store.commit('purchaseOrderItems/setItems', []);
      },
      openPrintModal() {
        this.$root.$emit('openPrintModal', this.printSettings);
      },
      openHistoryModal() {
        this.historySettings.title = this.purchaseOrderData.order_nr;
        this.$root.$emit('openHistoryModal', this.historySettings);
      }
    },
    computed: {
      ...mapState('generic', ['incoterms', 'currentRoutePermissions']),
      ...mapState('partners', ['suppliers', 'customers', 'agents']),
      ...mapState('woodware', ['measure_types', 'delivery_methods']),
      ...mapState('purchaseOrderItems', ['orderItems', 'orderItemsColumns', 'pinnedRowData']),
      ...mapGetters('purchaseOrderItems', {
        orderItemsGridOptions: 'gridOptions',
      }),
      ...mapState('purchaseOrders', ['documents', 'comments', 'progressPerMovementColumns', 'progressPerMovement', 'gridOptions']),
      ...mapGetters('purchaseOrders', {
        gridOptions: 'gridOptions'
      }),
      priceUsageIsPriceList () {
        return this.purchaseOrderData.shipper.price_usage === 'price_list'
      },
      param() {
        return this.$route.params.id;
      },
      commonUrl() {
        return `purchase/orders/${this.param}/`;
      },
      isActiveOrder() {
        return (this.purchaseOrderData.status === 'active' || this.isNewOrder) ? true : false;
      },
      purchaseOrderLabel() {
        return this.isNewOrder ? `${this.$i18n.t('common_content.new')} ${this.$i18n.t('common_content.purchase_order')}`
          : `${this.$i18n.t('common_content.purchase_order')} / ${this.purchaseOrderData.order_nr}`
      },
      purchaseOrderInfo() {
        return this.isNewOrder ? '' : `${this.purchaseOrderData.created} / ${this.$i18n.t('common_content.creator')}:
        ${this.purchaseOrderData.created_by}`
      },
      isNewOrder() {
        return this.param === 'new';
      },
      printSettings() {
        return {url: `purchase/purchase_orders/${this.param}/print/`, allowChanges: true, isFileReport: true};
      },
      historySettings() {
        return {url: `purchase_order/?id=${this.param}`, title: ``};
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return this.showCollapse ? {height: 'calc(100vh - 550px)'} : {height: 'calc(100vh - 300px)'};
        } else {
          return this.showCollapse ? {height: '400px'} : {height: 'calc(100vh - 300px)'};
        }

      }
    },
    async beforeMount() {
      this.$store.dispatch('partners/getSuppliers');
      this.$store.dispatch('partners/getSaleAgents');
      await this.$store.dispatch('partners/getCustomers');
      await this.$store.cache.dispatch('generic/loadIncoterms');
      await this.$store.cache.dispatch('woodware/getDeliveryMethods');
      if (this.isNewOrder) {
        this.purchaseOrderData.buyer = this.customers.find(e => e.id === 1458);
        this.purchaseOrderData.incoterms = this.incoterms.find(e => e.id === 4);
        this.purchaseOrderData.delivery_method = this.delivery_methods.find(e => e.id === 2);
      }
      if (!this.isNewOrder) {
        this.getOrderInfo(this.param);
        this.$store.dispatch('purchaseOrders/loadDocumentsList', {id: this.param});
        this.$store.dispatch('purchaseOrders/loadCommentsList', {id: this.param});
      }
    },
    mounted() {
      this.$root.$on('updatePurchaseOrderInfo', (payLoad) => {
        this.getOrderInfo(payLoad.id);
      })
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    beforeDestroy() {
      this.$root.$off('updatePurchaseOrderInfo');
    }
  }
</script>

<style scoped>

  .grid {
    height: 350px;
  }

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/orders/PurchaseOrder.vue